/**
 * useDetailButtons hook.
 */
import Context from '@shared/components/utils/context/detailContext';
import Focus from '@shared/types/enums/focus';
import GenericButton from '../../../../src/components/elements/buttons/ui/genericButton';
import IconButton from '../../../../src/components/elements/buttons/ui/iconButton';
import Modals from '@shared/types/enums/modals';
import PlayIcon from '@components/atoms/icons/play';
import PressableProps from '@shared/types/props/pressable';
import TranslationManager from '@shared/assets/locale/translationsManager';
import UpgradeButton from '../../../../src/components/elements/buttons/ui/upgradeButton';
import goToContent from '@shared/actions/content/goToContent';
import { loginAction } from '@shared/actions/flow';
import { pushFocus, setFocus } from '@shared/store/slices/focus.slice';
import { setModal, setModalParams } from '@shared/store/slices/modal.slice';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { useCallback, useContext, useMemo } from 'react';
import { userSelector } from '@shared/store/slices/user.slice';

/**
 * Constants.
 *
 * @constant {string} LITERALS - Detail page literals.
 */
const LITERALS = 'PAGES.DETAIL';

/**
 * Detail button interface.
 */
interface DetailButton {
  Button: (parentProps: PressableProps) => JSX.Element;
  Icon?: ({ focused }: { focused: boolean }) => JSX.Element | null;
  action: () => void;
  text: string;
}

/**
 * Custom hook for the detail buttons.
 */
export default function useDetailButtons() {
  // ------------------------------------------------------------
  // Variables
  // ------------------------------------------------------------

  // Dispatch
  const dispatch = useAppDispatch();
  const { asset, focusable, metadata, upgradablePlan } = useContext(Context);
  const { isAnonymous } = useAppSelector(userSelector);

  // ------------------------------------------------------------
  // Event handlers
  // ------------------------------------------------------------

  /**
   * Handles the mouse enter event. Focuses the detail buttons section.
   */
  const onMouseEnter = useCallback(() => {
    focusable.current.buttons && dispatch(setFocus(Focus.DETAIL_BUTTONS));
  }, []);

  /**
   * Handles the out of bounds event. Pushes the menu focus.
   */
  const outOfBounds = useCallback(
    (_focus: Focus | number | string, exit?: string) => {
      if (exit === 'LEFT') {
        dispatch(pushFocus(Focus.MENU));
      }
    },
    []
  );

  // ------------------------------------------------------------
  // Actions
  // ------------------------------------------------------------

  /**
   * Displays the anonymous modal.
   */
  const displayAnonymousModal = useCallback(() => {
    dispatch(setModal(Modals.ANONYMOUS_GENERIC));
  }, []);

  /**
   * Displays the upgrade plan modal.
   */
  const displayUpgradePlanModal = useCallback(() => {
    dispatch(
      setModalParams({
        plan: upgradablePlan,
        channel: asset?.channel,
      })
    );
    dispatch(setModal(Modals.UPGRADE_PLAN));
  }, [asset]);

  const playAction = useCallback(() => {
    goToContent(asset);
  }, [asset]);

  // ------------------------------------------------------------
  // Memoized values
  // ------------------------------------------------------------

  /**
   * Memoized buttons.
   */
  const buttons: DetailButton[] = useMemo(() => {
    if (!metadata) return [];
    const displayAnonymous = isAnonymous && upgradablePlan;
    const displayAuthenticatedButtons =
      metadata?.isCurrentDate || (metadata?.isPastDate && metadata.catchup);
    const buttons = displayAnonymous
      ? [
          {
            Button: UpgradeButton,
            action: displayAnonymousModal,
            text: TranslationManager.get(`${LITERALS}.BUTTONS.SUBSCRIBE`),
          },
          {
            Button: GenericButton,
            action: loginAction,
            text: TranslationManager.get(`${LITERALS}.BUTTONS.LOGIN`),
          },
        ]
      : displayAuthenticatedButtons
      ? [
          upgradablePlan
            ? {
                Button: UpgradeButton,
                action: displayUpgradePlanModal,
                text: TranslationManager.get(
                  `${LITERALS}.BUTTONS.WANT_TO_WATCH`
                ),
              }
            : {
                Button: IconButton,
                action: playAction,
                Icon: PlayIcon,
                text: TranslationManager.get(`${LITERALS}.BUTTONS.WATCH_NOW`),
              },
        ]
      : [];
    if (buttons.length) {
      focusable.current.buttons = true;
    }
    return buttons;
  }, [asset, isAnonymous, metadata, upgradablePlan]);

  /**
   * Memoized return value.
   */
  return useMemo(() => {
    return {
      buttons,
      callbacks: {
        container: {
          onMouseEnter,
        },
        list: {
          outOfBounds,
        },
      },
      props: {
        buttons: {
          hasMargin: !!upgradablePlan,
        },
        list: {
          focus: Focus.DETAIL_BUTTONS,
        },
      },
    };
  }, [asset, buttons, isAnonymous, metadata, upgradablePlan]);
}
